<template>
  <div class="edit-live-review-tracker-table">
    <div class="is-flex is-align-items-center is-justify-content-space-between mb-2">
      <h3 class="title is-6 mb-0">Balises</h3>
      <b-input
        v-model="keywords"
        size="is-small"
        type="search"
        icon="magnifying-glass"
        placeholder="Balise ou dossard"
      />
    </div>

    <o-table
      :items="visibleTrackers"
      :loading="loading"
      :draggable="false"
      checkable
      :checkedRows="checkedRows"
      :isRowChecked="isSameTracker"
      defaultSort="delayedCount"
      defaultSortDirection="desc"
      :paginated="false"
      :stickyHeader="true"
      @check="onCheck"
    >
      <b-table-column v-slot="{ row }" field="label" label="Balise" sortable cell-class="is-narrow">
        <router-link v-if="row.route" target="_blank" :to="row.route">
          {{ row.label }}
        </router-link>
        <span v-else>{{ row.label }}</span>
      </b-table-column>
      <b-table-column
        v-slot="{ row }"
        field="delayedCount"
        label="Points en retard"
        sortable
        numeric
      >
        {{ row.delayedCount === null ? EMPTY_VALUE : row.delayedCount }}
      </b-table-column>
      <b-table-column v-slot="{ row }" field="totalCount" label="Points" sortable numeric>
        {{ row.totalCount === null ? EMPTY_VALUE : row.totalCount }}
      </b-table-column>
      <b-table-column
        v-slot="{ row }"
        field="medianDelaySeconds"
        label="Retard médian"
        sortable
        numeric
        :customSort="compareByMedianDelay"
      >
        {{ row.medianDelaySeconds === null ? EMPTY_VALUE : formatDelay(row.medianDelaySeconds) }}
      </b-table-column>

      <template #empty>
        <span class="is-flex is-justify-content-center">Aucune balise sur cette étape</span>
      </template>
    </o-table>
  </div>
</template>

<script>
import OTable from '@components/Table'
import { stringNormalizer } from '@helpers/stringNormalizer'

const EMPTY_VALUE = '—'

const formatDelay = (seconds) => {
  if (seconds < 60) {
    return `${seconds} s`
  }

  const minutes = Math.floor(seconds / 60)

  if (minutes < 60) {
    return `${minutes} min ${String(seconds % 60).padStart(2, '0')} s`
  }

  return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')}`
}

const compareByMedianDelay = (left, right, isAscending) => {
  if (left.medianDelaySeconds === null || right.medianDelaySeconds === null) {
    return (left.medianDelaySeconds === null ? 1 : 0) - (right.medianDelaySeconds === null ? 1 : 0)
  }

  return isAscending
    ? left.medianDelaySeconds - right.medianDelaySeconds
    : right.medianDelaySeconds - left.medianDelaySeconds
}

export default {
  name: 'EditLiveReviewTrackerTable',

  components: {
    OTable,
  },

  model: {
    prop: 'selectedTrackerIds',
    event: 'input',
  },

  props: {
    trackers: {
      type: Array,
      required: true,
      validator: (value) =>
        value.every((tracker) =>
          ['trackerId', 'label', 'medianDelaySeconds', 'delayedCount', 'totalCount'].every(
            (key) => key in tracker,
          ),
        ),
    },
    selectedTrackerIds: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      keywords: '',
    }
  },

  created() {
    this.EMPTY_VALUE = EMPTY_VALUE
    this.compareByMedianDelay = compareByMedianDelay
    this.formatDelay = formatDelay
  },

  computed: {
    visibleTrackers() {
      const keywords = stringNormalizer(this.keywords).trim().toLowerCase()

      if (!keywords) {
        return this.trackers
      }

      return this.trackers.filter(({ label }) =>
        stringNormalizer(label || '')
          .toLowerCase()
          .includes(keywords),
      )
    },

    checkedRows() {
      return this.trackers.filter(({ trackerId }) => this.selectedTrackerIds.includes(trackerId))
    },
  },

  methods: {
    isSameTracker(left, right) {
      return left.trackerId === right.trackerId
    },

    onCheck(rows) {
      this.$emit(
        'input',
        rows.map(({ trackerId }) => trackerId),
      )
    },
  },
}
</script>

<style lang="scss">
@import 'variables';

$table-height-mobile: 320px;
$table-height: 500px;

.edit-live-review-tracker-table {
  .o-table .b-table .table-wrapper {
    max-height: $table-height-mobile;
  }

  @include desktop {
    .o-table .b-table .table-wrapper {
      min-height: $table-height;
      max-height: $table-height;
    }
  }
}
</style>
